import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { en, type TranslationKey } from './locales/en';
import { es } from './locales/es';

export type Locale = 'en' | 'es';

const DICTIONARIES: Record<Locale, Record<string, string>> = {
  en,
  es: es as Record<string, string>,
};

const LOCALE_META: Record<Locale, { label: string; flag: string }> = {
  en: { label: 'English', flag: '' },
  es: { label: 'Español', flag: '' },
};

interface I18nCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: TranslationKey) => string;
  availableLocales: { code: Locale; label: string; flag: string }[];
}

const Ctx = createContext<I18nCtx | null>(null);

function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  const saved = localStorage.getItem('myday-locale') as Locale | null;
  if (saved && DICTIONARIES[saved]) return saved;
  const nav = navigator.language?.slice(0, 2).toLowerCase();
  return nav === 'es' ? 'es' : 'en';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectInitialLocale());

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { localStorage.setItem('myday-locale', l); } catch {}
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback((key: TranslationKey) => {
    const dict = DICTIONARIES[locale];
    return dict?.[key] ?? en[key] ?? key;
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    setLocale,
    t,
    availableLocales: (Object.keys(DICTIONARIES) as Locale[]).map((code) => ({
      code, ...LOCALE_META[code],
    })),
  }), [locale, setLocale, t]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}
